import * as React from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Stack,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import { dateTimeString } from '../../../core/functions/dateTime'
import AffiliatesFunction from '../../../core/functions/affiliates'
import { TAffiliated, TAffiliatedStatus } from '../../../core/types/Affiliated'
import MyAffiliationsDetails from './MyAffiliationsDetails'
import AffiliationController from '../../../core/controllers/AffiliationController'
import { useMyAffiliations } from '../../../core/hooks/useMyAffiliations'
import InputSearch from '../../../components/Inputs/InputSearch'
import SuccessSnackbar from '../../../components/Snackbar/SuccessSnackbar'
import LoadingBackdrop from '../../../components/Loading/LoadingBackdrop'
import ErrorSnackbar from '../../../components/Snackbar/ErrorSnackbar'
import CopiedSnackbar from '../../../components/Snackbar/CopiedSnackbar'
import AlertDefault from '../../../components/Alerts'
import DataTable, {
  IDataTableColumn,
} from '../../../components/Tables/DataTable'
import MyAffiliationsMobile from './MyAffiliationsMobile'

export default function MyAffiliations() {
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState<TAffiliatedStatus>(
    TAffiliatedStatus.All
  )
  const [current, setCurrent] = React.useState<TAffiliated | undefined>(
    undefined
  )
  const [open, setOpen] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [success, setSuccess] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [errorSending, setErrorSending] = React.useState('')

  const oldSearch = React.useRef('')

  const { loading, data, error, execute, clear } = useMyAffiliations({
    page,
    rows: rowsPerPage,
    search,
    status,
  })

  const columns: readonly IDataTableColumn<TAffiliated>[] = [
    {
      id: 'createdDate',
      label: 'Data',
      minWidth: 30,
      format: (value) => dateTimeString(value),
    },
    {
      id: 'productActive',
      label: 'Ativo',
      minWidth: 40,
      format: (value) => (value ? 'Sim' : 'Não'),
    },
    { id: 'productName', label: 'Nome', minWidth: 50 },
    {
      id: 'commissionPercentage',
      label: 'Comissão',
      minWidth: 40,
      format: (value) => `${value}%`,
    },
    {
      id: 'globalCommissionPercentage',
      label: 'Comissão Geral',
      minWidth: 40,
      format: (value) => `${value}%`,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 40,
      render(id, value, row) {
        const statusMap = AffiliatesFunction.getStatusMap(value)
        const { label, ...style } = statusMap
        return <Chip size="small" label={label} sx={{ ...style }} />
      },
    },
  ]

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows)
    setPage(0)
  }

  const handleChangeItem = (affiliated: TAffiliated) => {
    if (!affiliated.productActive) {
      return
    }
    setCurrent(affiliated)
    setOpen(true)
  }

  const handleClose = () => {
    setCurrent(undefined)
    setOpen(false)
  }

  const handleChangeSave = async () => {
    if (current) {
      setSending(true)
      try {
        const response = await AffiliationController.updateAffiliated({
          data: current,
        })
        if (!response.success) {
          setErrorSending(response.error)
          return
        }
        setSuccess(true)
        handleClose()
        execute()
      } finally {
        setSending(false)
      }
    }
  }

  React.useEffect(() => {
    if (data.rowsPerPage > 0 && data.rowsPerPage !== rowsPerPage) {
      setRowsPerPage(data.rowsPerPage)
    }

    if (oldSearch.current !== search) {
      oldSearch.current = search
      setPage(0)
    }
  }, [data])

  return (
    <Box width="100%">
      {error !== '' && (
        <Box sx={{ mb: 2 }}>
          <AlertDefault onClose={clear} severity="error" sx={{ width: '100%' }}>
            {error}
          </AlertDefault>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <InputSearch
          id="searchMyAffiliations"
          name="Filtrar por nome"
          setSearch={setSearch}
          execute={execute}
        />

        <FormControl sx={{ width: '20%' }}>
          <InputLabel id="days-select-label">Status</InputLabel>
          <Select
            variant="outlined"
            labelId="days-select-label"
            label="Status"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
          >
            <MenuItem value={TAffiliatedStatus.All}>Todos</MenuItem>
            <MenuItem value={TAffiliatedStatus.Pending}>
              {AffiliatesFunction.descriptionAffiliatedStatus(
                TAffiliatedStatus.Pending
              )}
            </MenuItem>
            <MenuItem value={TAffiliatedStatus.Accepted}>
              {AffiliatesFunction.descriptionAffiliatedStatus(
                TAffiliatedStatus.Accepted
              )}
            </MenuItem>
            <MenuItem value={TAffiliatedStatus.Refused}>
              {AffiliatesFunction.descriptionAffiliatedStatus(
                TAffiliatedStatus.Refused
              )}
            </MenuItem>
            <MenuItem value={TAffiliatedStatus.Blocked}>
              {AffiliatesFunction.descriptionAffiliatedStatus(
                TAffiliatedStatus.Blocked
              )}
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <DataTable
        columns={columns}
        data={data}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onItemClick={handleChangeItem}
        renderItemMobile={(item) => (
          <MyAffiliationsMobile
            affiliated={item}
            handleChangeItem={handleChangeItem}
          />
        )}
      />

      <MyAffiliationsDetails
        affiliated={current}
        open={open}
        sending={sending}
        handleClose={handleClose}
        setOpen={setOpen}
        setAffiliated={setCurrent}
        saveAffiliated={handleChangeSave}
        setCopied={setCopied}
        setErrorSending={setErrorSending}
        setSending={setSending}
        setSuccess={setSuccess}
        reload={execute}
      />

      <LoadingBackdrop open={loading || sending} />

      <ErrorSnackbar open={error !== ''} onClose={() => setErrorSending('')}>
        {error}
      </ErrorSnackbar>

      <SuccessSnackbar open={success} onClose={() => setSuccess(false)}>
        Atualizado com sucesso
      </SuccessSnackbar>

      <CopiedSnackbar open={copied} onClose={() => setCopied(false)} />
    </Box>
  )
}
